import type {
    EditionFragment as DripEdition,
    SmsDripEditionFragment as SmsDripEdition,
    DripSequenceFragment as DripSequence,
    SmsDripSequenceFragment as SmsDripSequence,
} from 'nutshell-graphql-types';

import {MAX_CHARACTERS, MIN_CHARACTERS} from '../../../sms/consts';

export function editionHasAllRequiredFields(edition: SmsDripEdition | DripEdition): boolean {
    return edition.__typename === 'Edition'
        ? emailEditionHasAllRequiredFields(edition)
        : smsEditionHasAllRequiredFields(edition);
}

function emailEditionHasAllRequiredFields(edition: DripEdition): boolean {
    return Boolean(
        edition &&
            edition.emailTemplate &&
            edition.emailTemplate.subject &&
            edition.hasUnsubscribeLink &&
            edition.hasStreetAddress
    );
}

function smsEditionHasAllRequiredFields(edition: SmsDripEdition): boolean {
    if (edition.hasShortLink) {
        return false;
    }

    return edition.content.length <= MAX_CHARACTERS && MIN_CHARACTERS <= edition.content.length;
}

export type FromAddress = {
    senderAddress: string | null | undefined;
    isSenderVerified: boolean;
    isDmarcVerified: boolean;
};

export const getFromAddress = (dripSequence: DripSequence): FromAddress => {
    const senderAddress =
        dripSequence.from &&
        dripSequence.from.emailAddress &&
        dripSequence.from.emailAddress.formatted
            ? dripSequence.from.emailAddress.formatted
            : undefined;

    const isSenderVerified = Boolean(dripSequence.from && dripSequence.from.status === 'VERIFIED');
    const isDmarcVerified = Boolean(
        dripSequence.from &&
            dripSequence.from.fromDomain &&
            dripSequence.from.fromDomain.refreshedDmarcStatus === 'STATUS_VERIFIED'
    );

    return {senderAddress, isSenderVerified, isDmarcVerified};
};

export const getEditionsMissingDelays = (editions: DripEdition[] | SmsDripEdition[]): string[] => {
    // @ts-expect-error
    const editionsIds = [];
    editions.forEach((edition) => {
        if (!edition.isFirstEdition && !edition.delay) {
            editionsIds.push(edition.id);
        }
    });

    // @ts-expect-error
    return editionsIds;
};

type RequiredCheckResult = {
    hasSequenceErrors: boolean;
    shouldDisplayReviewSequence: boolean;
};

export function performRequiredSequenceChecks(
    dripSequence: DripSequence | SmsDripSequence,
    editionsMissingDelays: string[]
): RequiredCheckResult {
    return dripSequence.__typename === 'Campaign'
        ? performRequiredEmailSequenceChecks(dripSequence, editionsMissingDelays)
        : performRequiredSmsSequenceChecks(dripSequence, editionsMissingDelays);
}

function performRequiredEmailSequenceChecks(
    dripSequence: DripSequence,
    editionsMissingDelays: string[]
): RequiredCheckResult {
    const fromAddress = getFromAddress(dripSequence);
    const isMissingFromAddress = !fromAddress.senderAddress || !fromAddress.isSenderVerified;
    const hasClickDomainProblems =
        dripSequence.customClickDomain && !dripSequence.customClickDomain.isVerified;
    const isMissingDmarc = !fromAddress.isDmarcVerified;

    // Determines if the 'review-sequences' modal may be dismissed
    const hasSequenceErrors = Boolean(
        editionsMissingDelays.length ||
            isMissingFromAddress ||
            hasClickDomainProblems ||
            (isMissingDmarc && NutClientConfig.enforceDmarcSetup)
    );

    // Determines if 'review-sequences' modal should be displayed
    const shouldDisplayReviewSequence = hasSequenceErrors || isMissingDmarc;

    return {
        hasSequenceErrors,
        shouldDisplayReviewSequence,
    };
}

function performRequiredSmsSequenceChecks(
    dripSequence: SmsDripSequence,
    editionsMissingDelays: string[]
): RequiredCheckResult {
    const isMissingFrom = !dripSequence.from;

    const hasSequenceErrors = Boolean(
        editionsMissingDelays.length || !dripSequence.startTrigger || isMissingFrom
    );
    const shouldDisplayReviewSequence = hasSequenceErrors;

    return {
        hasSequenceErrors,
        shouldDisplayReviewSequence,
    };
}
